/** @jsx jsx */
import React, { type RefCallback } from 'react';
import { jsx } from '@compiled/react';
import { Flex } from '@atlaskit/primitives/compiled';
import Button from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { token } from '@atlaskit/tokens';
import type { PopupSelectProps } from '@atlaskit/select';
import { cssMap } from '@atlaskit/css';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import type { NullableOption } from '../types.tsx';
import messages from './messages.tsx';

const GENERIC_ICON_URL = '/icons/issuetypes/genericissue.svg';

type PopupSelectTriggerProps = Omit<
	Parameters<Exclude<PopupSelectProps['target'], undefined>>[0],
	'ref'
>;

export type FooterProps = PopupSelectTriggerProps & {
	isOpen: boolean;
	triggerRef: RefCallback<HTMLElement>;
	value?: NullableOption;
	isDisabled?: boolean;
};

export const Trigger = ({ isOpen, triggerRef, value, ...triggerProps }: FooterProps) => {
	const { formatMessage } = useIntl();

	return (
		<Tooltip content={value?.label ?? null} delay={500} hideTooltipOnClick>
			<Button
				{...triggerProps}
				aria-label={formatMessage(messages.label, { issueType: value?.label })}
				appearance="subtle"
				isSelected={isOpen}
				ref={triggerRef}
				spacing="compact"
			>
				<Flex alignItems="center" justifyContent="center" gap="space.075">
					<img css={imgStyles.root} src={value?.avatar?.small || GENERIC_ICON_URL} alt="" />
					<span css={wrapperStyles.root}>
						{isOpen ? (
							<ChevronUpIcon label="" color={token('color.icon')} size="small" />
						) : (
							<ChevronDownIcon label="" color={token('color.icon')} size="small" />
						)}
					</span>
				</Flex>
			</Button>
		</Tooltip>
	);
};

const imgStyles = cssMap({
	root: {
		height: '16px',
		width: '16px',
		marginTop: token('space.025'),
	},
});

// Required for issue-table, as it adds an eventlistener that checks if the event target was part of the ICC form
// Therefore we need the event target to be the button so that it remains in the DOM
const wrapperStyles = cssMap({
	root: {
		pointerEvents: 'none',
	},
});
